import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Clock, Users, TrendingUp, TrendingDown, AlertTriangle,
  Calendar, ChevronDown, ChevronUp, BarChart3
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

/**
 * JobLaborSummary Component
 * Shows estimated vs actual on-site labor hours for admin visibility
 */
export function JobLaborSummary({ 
  job, 
  apiClient,
  showDetails = true
}) {
  const [laborData, setLaborData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (job?.id) {
      fetchLaborSummary();
    }
  }, [job?.id]);

  const fetchLaborSummary = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/jobs/${job.id}/labor-summary`);
      setLaborData(res.data);
    } catch (err) {
      console.error('Failed to fetch labor summary:', err);
      setLaborData(null);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <Card className="bg-zinc-800 border-zinc-700">
        <CardContent className="py-6 flex justify-center">
          <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
        </CardContent>
      </Card>
    );
  }

  if (!laborData) {
    return (
      <Card className="bg-zinc-800 border-zinc-700">
        <CardContent className="py-4">
          <p className="text-zinc-500 text-sm text-center">Unable to load labor summary</p>
        </CardContent>
      </Card>
    );
  }

  const { 
    estimated_hours, 
    actual_hours, 
    hours_variance, 
    variance_percent,
    status,
    hours_by_employee,
    hours_by_date,
    expected_labor_phases
  } = laborData;

  const progressPercent = estimated_hours > 0 
    ? Math.min((actual_hours / estimated_hours) * 100, 150) 
    : 0;
  
  const isOverBudget = actual_hours > estimated_hours;
  const hoursRemaining = Math.max(0, estimated_hours - actual_hours);

  return (
    <Card className="bg-zinc-800 border-zinc-700">
      <CardHeader 
        className="pb-2 cursor-pointer" 
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <Clock className="w-4 h-4 text-blue-400" />
            Labor Hours Tracking
          </CardTitle>
          <div className="flex items-center gap-2">
            {isOverBudget ? (
              <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                <TrendingUp className="w-3 h-3 mr-1" />
                Over by {Math.abs(hours_variance).toFixed(1)}h
              </Badge>
            ) : hoursRemaining > 0 ? (
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                <TrendingDown className="w-3 h-3 mr-1" />
                {hoursRemaining.toFixed(1)}h remaining
              </Badge>
            ) : (
              <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                On Target
              </Badge>
            )}
            {expanded ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-2">
        {/* Main Stats */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-zinc-900/50 p-3 rounded-lg">
            <p className="text-xs text-zinc-400 uppercase">Estimated Hours</p>
            <p className="text-2xl font-bold text-zinc-300">
              {estimated_hours?.toFixed(1) || '0'}h
            </p>
            {expected_labor_phases?.length > 0 && (
              <p className="text-xs text-zinc-500">
                From {expected_labor_phases.length} phase(s)
              </p>
            )}
          </div>
          <div className={`p-3 rounded-lg ${isOverBudget ? 'bg-red-900/20' : 'bg-green-900/20'}`}>
            <p className="text-xs text-zinc-400 uppercase">Actual Hours</p>
            <p className={`text-2xl font-bold ${isOverBudget ? 'text-red-400' : 'text-green-400'}`}>
              {actual_hours?.toFixed(1) || '0'}h
            </p>
            {hours_by_employee?.length > 0 && (
              <p className="text-xs text-zinc-500">
                From {hours_by_employee.length} employee(s)
              </p>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between text-xs text-zinc-400 mb-1">
            <span>Progress</span>
            <span>
              {actual_hours?.toFixed(1)}h / {estimated_hours?.toFixed(1)}h
              {variance_percent !== 0 && (
                <span className={isOverBudget ? 'text-red-400 ml-1' : 'text-green-400 ml-1'}>
                  ({isOverBudget ? '+' : ''}{variance_percent?.toFixed(1)}%)
                </span>
              )}
            </span>
          </div>
          <div className="w-full bg-zinc-700 rounded-full h-2.5 overflow-hidden">
            <div 
              className={`h-2.5 rounded-full transition-all ${
                progressPercent > 100 ? 'bg-red-500' : 
                progressPercent > 80 ? 'bg-yellow-500' : 
                'bg-green-500'
              }`}
              style={{ width: `${Math.min(progressPercent, 100)}%` }}
            />
            {progressPercent > 100 && (
              <div 
                className="h-2.5 bg-red-400/50 -mt-2.5"
                style={{ width: `${Math.min(progressPercent - 100, 50)}%`, marginLeft: '100%', transform: 'translateX(-100%)' }}
              />
            )}
          </div>
          {progressPercent > 100 && (
            <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Over budget by {Math.abs(hours_variance).toFixed(1)} hours
            </p>
          )}
        </div>

        {/* Expanded Details */}
        {expanded && showDetails && (
          <div className="space-y-4 pt-4 border-t border-zinc-700">
            {/* Hours by Employee */}
            {hours_by_employee?.length > 0 && (
              <div>
                <p className="text-xs text-zinc-400 uppercase mb-2 flex items-center gap-1">
                  <Users className="w-3 h-3" /> Hours by Employee
                </p>
                <div className="space-y-2">
                  {hours_by_employee.map((emp, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-zinc-900/50 p-2 rounded">
                      <span className="text-sm text-white">{emp.employee}</span>
                      <div className="text-right">
                        <span className="text-sm font-medium text-primary">{emp.hours?.toFixed(1)}h</span>
                        <span className="text-xs text-zinc-500 ml-2">{emp.entries} entries</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Hours by Date */}
            {hours_by_date?.length > 0 && (
              <div>
                <p className="text-xs text-zinc-400 uppercase mb-2 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Hours by Date
                </p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {hours_by_date.slice(0, 7).map((day, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-zinc-900/50 p-2 rounded">
                      <span className="text-sm text-zinc-300">
                        {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </span>
                      <span className="text-sm font-medium text-primary">{day.hours?.toFixed(1)}h</span>
                    </div>
                  ))}
                  {hours_by_date.length > 7 && (
                    <p className="text-xs text-zinc-500 text-center">
                      +{hours_by_date.length - 7} more days
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Expected Labor Phases */}
            {expected_labor_phases?.length > 0 && (
              <div>
                <p className="text-xs text-zinc-400 uppercase mb-2 flex items-center gap-1">
                  <BarChart3 className="w-3 h-3" /> Estimated Labor Breakdown
                </p>
                <div className="space-y-2">
                  {expected_labor_phases.map((phase, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-zinc-900/50 p-2 rounded">
                      <span className="text-sm text-zinc-300">{phase.phase_name || 'General'}</span>
                      <div className="text-right">
                        <span className="text-sm font-medium text-zinc-300">{phase.expected_hours?.toFixed(1)}h</span>
                        <span className="text-xs text-zinc-500 ml-2">@ ${phase.hourly_rate}/hr</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Compact View Toggle */}
        {!expanded && showDetails && (hours_by_employee?.length > 0 || expected_labor_phases?.length > 0) && (
          <button 
            onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
            className="w-full text-center text-xs text-zinc-400 hover:text-primary mt-2"
          >
            Click to see details
          </button>
        )}
      </CardContent>
    </Card>
  );
}

export default JobLaborSummary;
